import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { ArrowRightIcon, BriefcaseIcon, SparklesIcon, CheckIcon } from 'lucide-react';
export const Hero = () => {
  const navigate = useNavigate();
  return <section id="hero" className="min-h-screen w-full flex flex-col justify-center relative overflow-hidden bg-purple-50" data-id="element-548">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-100 to-purple-50 rounded-full opacity-40 -mr-48 -mt-48" data-id="element-549"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tl from-purple-100 to-purple-50 rounded-full opacity-30 -ml-40 -mb-40" data-id="element-550"></div>

      <div className="container mx-auto px-4 md:px-6 py-24 pt-32 relative z-10 flex flex-col items-center" data-id="element-551">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-552">
          <p className="text-primary font-semibold tracking-wider uppercase text-sm mb-4" data-id="element-553">
            Ship AI agents in weeks. Start within 48 hours.
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-black mb-6 leading-tight" data-id="element-554">
            Build <span className="text-primary" data-id="element-555">AI agents & products</span>{' '}
            with elite leadership
          </h1>
          <p className="text-text-secondary text-lg md:text-xl" data-id="element-556">
            We embed senior AI operators in your team to ship agents, automate
            workflows, and lead your AI strategy without the full-time team
            hire.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-5xl mx-auto" data-id="element-557">
          {/* Path 1: Fractional Head of AI — PRIMARY OFFERING */}
          <div className="group bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-purple-900/5 hover:shadow-2xl hover:shadow-purple-900/10 transition-all duration-300 border-2 border-primary/40 hover:border-primary flex flex-col h-full relative overflow-hidden" data-id="element-558">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100 rounded-bl-full opacity-60 transition-transform group-hover:scale-110" data-id="element-559"></div>
            <div className="absolute top-5 right-5 z-20" data-id="element-560">
              <span className="bg-primary text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" data-id="element-561">
                Most in demand
              </span>
            </div>

            <div className="relative z-10 flex-grow" data-id="element-562">
              <div className="w-14 h-14 bg-primary text-white rounded-2xl flex items-center justify-center mb-6" data-id="element-563">
                <SparklesIcon size={28} data-id="element-564" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" data-id="element-565">
                Fractional
                <br data-id="element-566" />
                Head of AI
              </h2>
              <p className="text-text-secondary mb-8 text-lg" data-id="element-567">
                Senior AI leadership to build agents, ship AI features, and turn
                AI opportunity into production outcomes.
              </p>

              <ul className="space-y-3 mb-8" data-id="element-568">
                {['AI Agents & Workflow Automation', 'Opportunity Discovery & Strategy', 'Production AI Product Development'].map((item, i) => <li key={i} className="flex items-center text-black/80" data-id="element-569">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-570">
                      <CheckIcon className="w-3 h-3 text-primary" strokeWidth={3} data-id="element-571" />
                    </div>
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="mt-auto pt-6 relative z-10" data-id="element-572">
              <Button variant="primary" onClick={() => navigate('/fractional-head-of-ai')} className="w-full text-lg group-hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 py-4" data-id="element-573">
                Explore AI Services
                <ArrowRightIcon size={18} data-id="element-574" />
              </Button>
            </div>
          </div>

          {/* Path 2: Fractional Product Management — SECONDARY */}
          <div className="group bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-purple-900/5 hover:shadow-2xl hover:shadow-purple-900/10 transition-all duration-300 border border-purple-100 hover:border-primary/30 flex flex-col h-full relative overflow-hidden" data-id="element-575">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-50 rounded-bl-full opacity-50 transition-transform group-hover:scale-110" data-id="element-576"></div>

            <div className="relative z-10 flex-grow" data-id="element-577">
              <div className="w-14 h-14 bg-purple-100 text-primary rounded-2xl flex items-center justify-center mb-6" data-id="element-578">
                <BriefcaseIcon size={28} data-id="element-579" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" data-id="element-580">
                Fractional
                <br data-id="element-581" />
                Product Management
              </h2>
              <p className="text-text-secondary mb-8 text-lg" data-id="element-582">
                AI-fluent product leadership to define vision, streamline
                execution, and drive growth — without a full-time hire.
              </p>

              <ul className="space-y-3 mb-8" data-id="element-583">
                {['Product Strategy & Vision', 'Roadmap Development', 'Team Leadership & Mentoring'].map((item, i) => <li key={i} className="flex items-center text-black/80" data-id="element-584">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-585">
                      <CheckIcon className="w-3 h-3 text-primary" strokeWidth={3} data-id="element-586" />
                    </div>
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="mt-auto pt-6 relative z-10" data-id="element-587">
              <Button variant="secondary" onClick={() => navigate('/fractional-pm')} className="w-full text-lg flex items-center justify-center gap-2 py-4" data-id="element-588">
                Explore PM Services
                <ArrowRightIcon size={18} data-id="element-589" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};